/**
 * PairWall — full-screen overlay shown when the mini-app is not yet
 * paired with a Mac. Replaces the previous mock-chat demo-mode UX.
 *
 * Two flows:
 *   1. Inside Telegram → "Tap to connect" if we already have a saved
 *      WS URL (Mac side will verify the initData HMAC). Else still
 *      show the QR scanner so the user can bind first time.
 *   2. Open browser → URL + 6-char code inputs + Scan QR.
 *
 * Auto-closes itself by listening to atmosStore.onLink: once `paired`
 * the wall is removed from the DOM.
 */

import { Icon } from "./Icon";
import { atmosWS } from "../lib/atmosWS";
import { atmosStore } from "../lib/atmosStore";
import { scanQR } from "../lib/qrScanner";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function parsePairPayload(text: string): { u: string; c: string } | null {
  try {
    const url = new URL(text);
    const u = url.searchParams.get("u") ?? "";
    const c = url.searchParams.get("c") ?? "";
    if (u && c) return { u, c };
  } catch { /* fall through */ }
  return null;
}

export interface PairWallOpts {
  insideTelegram: boolean;
  userFirstName?: string;
}

export function PairWall(opts: PairWallOpts): HTMLElement {
  const root = el("div", "pair-wall");
  root.setAttribute("role", "dialog");
  root.setAttribute("aria-label", "Pair with your Mac");

  const card = el("div", "pair-wall__card");
  root.appendChild(card);

  const logo = el("div", "pair-wall__logo");
  logo.appendChild(Icon("atmos", 40));
  card.appendChild(logo);

  card.appendChild(el("h1", "pair-wall__title", "Connect to your Mac"));
  const sub = el("p", "pair-wall__sub");
  sub.textContent = opts.insideTelegram
    ? `Hi ${opts.userFirstName ?? "there"} — scan the QR shown in your Atmosphere Mac app, or paste the link manually.`
    : "Open Atmosphere on your Mac → Settings → Bridge server → scan QR with your phone, or paste the link.";
  card.appendChild(sub);

  // URL + code inputs.
  const form = el("div", "pair-wall__form");
  const urlIn = document.createElement("input");
  urlIn.className = "pair-wall__input";
  urlIn.type = "text";
  urlIn.placeholder = "wss://*.trycloudflare.com";
  urlIn.value = localStorage.getItem("atmos.ws_url") ?? "";
  urlIn.setAttribute("aria-label", "Mac bridge URL");
  form.appendChild(urlIn);

  const codeIn = document.createElement("input");
  codeIn.className = "pair-wall__input pair-wall__input--code";
  codeIn.type = "text";
  codeIn.placeholder = "ABC123";
  codeIn.maxLength = 6;
  codeIn.value = localStorage.getItem("atmos.pair_code") ?? "";
  codeIn.setAttribute("aria-label", "6-character pair code");
  codeIn.addEventListener("input", () => {
    codeIn.value = codeIn.value.toUpperCase();
  });
  form.appendChild(codeIn);
  card.appendChild(form);

  // Action buttons.
  const actions = el("div", "pair-wall__actions");

  const scanBtn = el("button", "pair-wall__btn pair-wall__btn--ghost");
  scanBtn.type = "button";
  scanBtn.appendChild(Icon("camera", 16));
  scanBtn.appendChild(document.createTextNode("Scan QR"));
  scanBtn.addEventListener("click", () => { void doScan(); });
  actions.appendChild(scanBtn);

  const connectBtn = el("button", "pair-wall__btn pair-wall__btn--primary");
  connectBtn.type = "button";
  connectBtn.appendChild(document.createTextNode("Connect"));
  connectBtn.addEventListener("click", () => doConnect());
  actions.appendChild(connectBtn);

  card.appendChild(actions);

  // Live status line — drives off atmosStore.
  const status = el("div", "pair-wall__status", "Not connected");
  card.appendChild(status);

  function renderStatus(): void {
    const s = atmosStore.linkState();
    const authErr = atmosStore.authErr();
    if (authErr) {
      status.textContent = `Auth failed: ${authErr}`;
      status.classList.add("pair-wall__status--err");
      return;
    }
    status.classList.remove("pair-wall__status--err");
    status.textContent =
      s === "connecting" ? "Connecting…" :
      s === "paired" ? "Paired ✓ — opening…" :
      s === "error" ? "Connection error — check URL/code" :
      "Not connected";
  }
  renderStatus();
  function teardown(): void {
    unsubLink();
    unsubAuth();
  }
  const unsubLink = atmosStore.onLink(() => {
    renderStatus();
    if (atmosStore.linkState() === "paired") {
      // Slight delay so the user sees the "Paired ✓" flash.
      setTimeout(() => { root.classList.add("pair-wall--gone"); }, 250);
      setTimeout(() => { teardown(); root.remove(); }, 600);
    }
  });
  const unsubAuth = atmosStore.onAuthErr(renderStatus);

  function doConnect(): void {
    const u = urlIn.value.trim();
    const c = codeIn.value.trim().toUpperCase();
    if (!u) { status.textContent = "Enter your Mac bridge URL first."; return; }
    if (!opts.insideTelegram && c.length !== 6) {
      status.textContent = "Pair code must be 6 characters.";
      return;
    }
    localStorage.setItem("atmos.ws_url", u);
    localStorage.setItem("atmos.pair_code", c);
    atmosWS.connect(u, c);
  }

  async function doScan(): Promise<void> {
    let text: string | null;
    try {
      text = await scanQR({ prompt: "Point at the Mac QR code" });
    } catch (err) {
      status.textContent = (err as Error).message;
      return;
    }
    if (!text) return;
    const parsed = parsePairPayload(text);
    if (!parsed) {
      status.textContent = "That QR isn't an atmos://pair link.";
      return;
    }
    urlIn.value = parsed.u;
    codeIn.value = parsed.c;
    doConnect();
  }

  return root;
}

export default PairWall;
